import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store";
import { useParams } from "react-router-dom";
import {
  clearAdminTonneDefektItem,
  getAdminTonneVerschwundenItemById,
  updateStatusById,
} from "../../store/slices/tonneVerschwunden";
import { setDataLoading } from "../../store/slices/admin";
import { format, getWeek } from "date-fns";
import { de } from "date-fns/locale";
import {
  AdminContentWrapper,
  AdminDetailInfoItem,
  AdminDetailPageHeader,
  AdminDetailsPageItemName,
  Button,
  Loader,
  History,
  CheckboxV2,
  CustomInputV2,
  TextAreaV2,
} from "../../components";
import transformHexToRgba from "../../util/transformHexToRgba";
import appConfig from "../../util/appConfig";
import DatePicker from "react-datepicker";
import style from "./style.module.scss";
import { statuses } from "../AdminTonneDefekt";

const AdminTonneDefektById = () => {
  const dispatch = useAppDispatch();
  const { id } = useParams();

  const { isAdminLoaderActive } = useAppSelector((state) => state.admin);
  const { isTonneVerschwundenDataLoading, tonneVerschwundenDataItem } =
    useAppSelector((state) => state.tonneVerschwunden.admin);

  const [selectedCheckbox, setSelectedCheckbox] = useState<
    undefined | "nextWeek" | "calenderWeek" | "toenisvorst" | "stornieren"
  >(undefined);
  const [calendarWeek, setCalendarWeek] = useState(0);
  const [toenisvorstWeekDay, setToenisvorstWeekDay] = useState("Montag");
  const [toenisvorstWeekDate, setToenisvorstWeekDate] = useState<Date | null>(
    null,
  );
  const [text, setText] = useState("");

  const generateCalenderWeek = (
    selectedCheckbox:
      | undefined
      | "nextWeek"
      | "calenderWeek"
      | "toenisvorst"
      | "stornieren",
  ) => {
    switch (selectedCheckbox) {
      case "nextWeek": {
        return (getWeek(new Date()) + 1).toString();
      }
      case "calenderWeek": {
        return calendarWeek.toString();
      }
      case "toenisvorst": {
        if (toenisvorstWeekDate) {
          return getWeek(new Date(toenisvorstWeekDate)).toString();
        }
        return undefined;
      }
      default: {
        return undefined;
      }
    }
  };
  const generateMessage = (
    selectedCheckbox:
      | undefined
      | "nextWeek"
      | "calenderWeek"
      | "toenisvorst"
      | "stornieren",
  ) => {
    switch (selectedCheckbox) {
      case "nextWeek": {
        return `Der Auftrag wird nächste Woche durchgeführt (in Kalenderwoche ${getWeek(new Date()) + 1}).`;
      }
      case "calenderWeek": {
        return `Der Auftrag wird voraussichtlich in Kalenderwoche ${calendarWeek} durchgeführt.`;
      }
      case "toenisvorst": {
        if (toenisvorstWeekDate) {
          return `Der Auftrag wird voraussichtlich am ${toenisvorstWeekDay} ${format(new Date(toenisvorstWeekDate), "dd.MM.yyyy")} durchgeführt.`;
        }
        return undefined;
      }
      case "stornieren": {
        return text;
      }
      default: {
        return undefined;
      }
    }
  };

  useEffect(() => {
    if (id) {
      dispatch(getAdminTonneVerschwundenItemById(id));
    }
    return () => {
      dispatch(clearAdminTonneDefektItem());
      dispatch(setDataLoading(false));
    };
  }, []);

  const items = [
    {
      title: "",
      items: [
        {
          rowTitle: "Status",
          rowInfo: statuses[tonneVerschwundenDataItem?.status!],
        },
        {
          rowTitle: "Datum (Meldung)",
          rowInfo: tonneVerschwundenDataItem
            ? format(
                new Date(tonneVerschwundenDataItem?.createdAt),
                "dd.MM.yyyy, HH:mm",
                {
                  locale: de,
                },
              )
            : "",
        },
        {
          rowTitle: "KW",
          rowInfo: tonneVerschwundenDataItem?.calenderWeek ?? "-",
        },
        {
          rowTitle: "Nachricht",
          rowInfo: tonneVerschwundenDataItem?.adminMessage ?? "-",
          isLast: true,
        },
      ],
    },
    {
      title: "Kontaktdaten",
      items: [
        {
          rowTitle: "Name",
          rowInfo: `${tonneVerschwundenDataItem?.userName} ${tonneVerschwundenDataItem?.userSurname}`,
        },
        {
          rowTitle: "Straße",
          rowInfo: `${tonneVerschwundenDataItem?.street}
                      ${tonneVerschwundenDataItem?.homeNumber}
                      ${
                        tonneVerschwundenDataItem?.hausNrZusatz
                          ? `${tonneVerschwundenDataItem?.hausNrZusatz}`
                          : ""
                      }`,
        },
        {
          rowTitle: "Ort",
          rowInfo: `${tonneVerschwundenDataItem?.place}`,
        },
        {
          rowTitle: "Telefonnummer",
          rowInfo: `${
            tonneVerschwundenDataItem?.phoneNumber
              ? "+" + tonneVerschwundenDataItem?.phoneNumber
              : "-"
          }`,
        },
        {
          rowTitle: "E-Mail",
          rowInfo: `${tonneVerschwundenDataItem?.email}`,
        },
        {
          rowTitle: "Kassenzeichen",
          rowInfo: `${tonneVerschwundenDataItem?.kassenzeichen ?? ""}`,
          isLast: true,
        },
      ],
    },
    {
      title: "Tonneninformation",
      items: [
        {
          rowTitle: "Objekt ID",
          rowInfo: `${tonneVerschwundenDataItem?.objectId ?? ""}`,
        },
        {
          rowTitle: "Tonnen ID's",
          rowInfo: ``,
          rowCustomItem: (
            <div>
              {tonneVerschwundenDataItem?.tonnenNummern.map((el) => {
                return <div>{el}</div>;
              })}
            </div>
          ),
        },
        {
          rowTitle: "Aufkleber",
          rowInfo: tonneVerschwundenDataItem?.verschwundeneTonne ?? "",
        },
        {
          rowTitle: "Tonnentyp",
          rowInfo: `${tonneVerschwundenDataItem?.tonnenType}`,
        },
        {
          rowTitle: "Tonnenvolumen",
          rowInfo: `${tonneVerschwundenDataItem?.tonnenVolume}L`,
        },
        {
          rowTitle: "Interne Behälternummer",
          rowInfo: `${tonneVerschwundenDataItem?.behid ?? '-'}`,
        },
        {
          rowTitle: "KAV-Nummer",
          rowInfo: `${tonneVerschwundenDataItem?.kavnr ?? '-'}`,
        },
      ],
    },
  ];

  const checkboxesToMap = [
    {
      onChange: () => {
        // setIsWeekSelected(true);
        setSelectedCheckbox("nextWeek");
      },
      text: "Der Auftrag wird nächste Woche durchgeführt.",
      value: selectedCheckbox === "nextWeek",
      withTextInput: false,
    },
    {
      onChange: () => {
        // setIsWeekSelected(false);
        setSelectedCheckbox("calenderWeek");
      },
      text: `Der Auftrag wird voraussichtlich in Kalenderwoche ${calendarWeek} durchgeführt.`,
      value: selectedCheckbox === "calenderWeek",
      withTextInput: true,
    },
    {
      onChange: () => {
        // setIsWeekSelected(false);
        setSelectedCheckbox("toenisvorst");
      },
      text: "Tönisvorst",
      value: selectedCheckbox === "toenisvorst",
      withCalendarInput: true,
    },
    {
      onChange: () => {
        // setIsWeekSelected(false);
        setSelectedCheckbox("stornieren");
      },
      text: "Stornieren",
      value: selectedCheckbox === "stornieren",
      withTextAreaInput: true,
    },
  ];

  return (
    <div
      style={{
        position: "relative",
      }}
    >
      <AdminDetailPageHeader isBtnAvailable={false} />
      <AdminContentWrapper>
        <AdminDetailsPageItemName text="Detailinformationen" />
        {isTonneVerschwundenDataLoading ? null : !tonneVerschwundenDataItem ? null : (
          <div>
            {items.map((el, idx) => {
              return (
                <AdminDetailInfoItem
                  items={el.items}
                  title={el.title}
                  key={idx}
                />
              );
            })}
            {tonneVerschwundenDataItem?.history?.length ? (
              <History history={tonneVerschwundenDataItem?.history} />
            ) : null}
            <div
              style={{
                display:
                  tonneVerschwundenDataItem?.status === "accepted"
                    ? "none"
                    : "flex",
                flexDirection: "column",
                gap: "20px",
                marginBottom: "20px",
              }}
            >
              {checkboxesToMap.map((el, idx) => {
                return (
                  <div
                    key={idx}
                    style={{
                      display: "flex",
                      gap: "40px",
                      flexDirection: "row",
                    }}
                  >
                    <CheckboxV2
                      onChange={el.onChange}
                      text={el.text}
                      value={el.value}
                      customStyle={{
                        maxWidth: "400px",
                        width: "100%",
                        flexGrow: 1,
                      }}
                      width="auto"
                    />
                    {el.withTextInput ? (
                      <CustomInputV2
                        id="calenderWeek"
                        label="Kalender Woche"
                        onChange={(v) => setCalendarWeek(+v)}
                        value={calendarWeek.toString()}
                        width="150"
                        isDisabled={selectedCheckbox !== "calenderWeek"}
                        regexp={/^[0-9]{0,2}$/}
                      />
                    ) : null}
                    {el.withCalendarInput ? (
                      <div
                        style={{
                          display: "flex",
                          gap: "20px",
                          alignItems: "center",
                        }}
                      >
                        <CustomInputV2
                          id="weekDay"
                          label="Tag der Woche"
                          onChange={(v) => setToenisvorstWeekDay(v)}
                          value={toenisvorstWeekDay}
                          width="150"
                          isDisabled={selectedCheckbox !== "toenisvorst"}
                        />
                        {/* <CustomDatePicker label="Datumpicker" name="toenisvorstWeekDate" type=""/> */}
                        <div
                          style={{
                            marginBottom: "20px",
                          }}
                        >
                          <label
                            translate="no"
                            className={`${style.label} ${selectedCheckbox !== "toenisvorst" ? style.disabled : ""}`}
                          >
                            Datum wählen
                          </label>
                          <DatePicker
                            onChange={(v) => setToenisvorstWeekDate(v)}
                            selected={toenisvorstWeekDate}
                            locale={"de"}
                            dateFormat="dd/MM/yyyy"
                            disabled={selectedCheckbox !== "toenisvorst"}
                            className={`${style.calendar} ${selectedCheckbox !== "toenisvorst" ? style.disabled : ""}`}
                          />
                        </div>
                      </div>
                    ) : null}
                    {el.withTextAreaInput ? (
                      <TextAreaV2
                        onChange={(v) => setText(v)}
                        value={text}
                        customStyle={{
                          flexShrink: 1,
                          flexGrow: 0,
                          width: "400px",
                        }}
                        isDisabled={selectedCheckbox !== "stornieren"}
                      />
                    ) : null}
                  </div>
                );
              })}
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Button
                text="Akzeptieren"
                width="200"
                isOutlined
                outlineColor={
                  tonneVerschwundenDataItem?.status === "accepted" ||
                  (selectedCheckbox === "calenderWeek" && calendarWeek === 0)
                    ? transformHexToRgba(appConfig.mainColor, "0.4")
                    : appConfig.mainColor
                }
                textColor={
                  tonneVerschwundenDataItem?.status === "accepted" ||
                  (selectedCheckbox === "calenderWeek" && calendarWeek === 0) ||
                  (selectedCheckbox === "toenisvorst" &&
                    (!toenisvorstWeekDate || !toenisvorstWeekDay)) ||
                  (selectedCheckbox === "stornieren" && !text)
                    ? transformHexToRgba(appConfig.mainColor, "0.4")
                    : appConfig.mainColor
                }
                disabled={
                  tonneVerschwundenDataItem?.status === "accepted" ||
                  (selectedCheckbox === "calenderWeek" && calendarWeek === 0)
                }
                marginRight="20"
                onClick={() => {
                  if (id) {
                    dispatch(
                      updateStatusById({
                        id: id,
                        status: "accepted",
                        message: generateMessage(selectedCheckbox),
                        calenderWeek: generateCalenderWeek(selectedCheckbox),
                        updatedStatusType: selectedCheckbox,
                      }),
                    );
                  }
                }}
              />
              {/* <Button
                text="Stornieren"
                width="200"
                backgroundColor="white"
                isOutlined
                outlineColor={
                  tonneVerschwundenDataItem?.status === "declined"
                    ? transformHexToRgba("#ff0000", "0.4")
                    : "red"
                }
                textColor={
                  tonneVerschwundenDataItem?.status === "declined"
                    ? transformHexToRgba("#ff0000", "0.4")
                    : "red"
                }
                disabled={tonneVerschwundenDataItem?.status === "declined"}
                marginRight="20"
                onClick={() => {
                  if (id) {
                    dispatch(
                      updateStatusById({
                        id: id,
                        status: "declined",
                      }),
                    );
                  }
                }}
              /> */}
            </div>
          </div>
        )}
      </AdminContentWrapper>
      {isAdminLoaderActive && <Loader />}
    </div>
  );
};

export default AdminTonneDefektById;
